// Model přípravy — přáteláky PL týmů (ESPN), minutáž hráčů + noví trenéři.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Barbell, ArrowsClockwise, Megaphone, NotePencil, TrendUp, TrendDown, ArrowRight } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScoutingTips() {
    const [db, setDb] = useState(null);
    useEffect(() => {
        axios.get(`${API}/preseason-db/overview`, { timeout: 30000 }).then((r) => setDb(r.data)).catch(() => {});
    }, []);
    if (!db || (!db.rising?.length && !db.falling?.length && !db.matches?.length)) return null;
    const winners = (db.matches || []).filter((m) => m.fpl_winners && !/nikdo/i.test(m.fpl_winners)).slice(0, 4);
    return (
        <section className="border border-fa-sky/30 bg-fa-sky/[0.04] p-4 space-y-3" data-testid="preseason-scouting-tips">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-display font-black uppercase text-base flex items-center gap-1.5">
                    <NotePencil size={14} className="text-fa-sky" /> Redakční scouting — tipy na hráče
                </h2>
                <Link to="/preseason/database" data-testid="preseason-db-link"
                      className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black flex items-center gap-1.5">
                    Celá databáze <ArrowRight size={10} />
                </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                {db.rising?.length > 0 && (
                    <div className="border border-fa-mint/30 bg-fa-mint/5 p-3" data-testid="preseason-tips-rising">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5 mb-1.5"><TrendUp size={11} /> Roste v přípravě</div>
                        <div className="space-y-1">
                            {db.rising.slice(0, 6).map((p, i) => (
                                <div key={i} className="font-mono text-[10px] flex items-baseline gap-1.5 flex-wrap">
                                    <b className="font-display text-[12px]">{p.player_name}</b>
                                    <Link to={`/preseason/club/${p.club_key}`} className="text-white/45 hover:text-fa-sky">{p.club}</Link>
                                    {p.notes && <span className="text-white/55 basis-full">{p.notes}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {db.falling?.length > 0 && (
                    <div className="border border-red-400/25 bg-red-400/5 p-3" data-testid="preseason-tips-falling">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-red-300 flex items-center gap-1.5 mb-1.5"><TrendDown size={11} /> Klesá / riziko</div>
                        <div className="space-y-1">
                            {db.falling.slice(0, 6).map((p, i) => (
                                <div key={i} className="font-mono text-[10px] flex items-baseline gap-1.5 flex-wrap">
                                    <b className="font-display text-[12px]">{p.player_name}</b>
                                    <Link to={`/preseason/club/${p.club_key}`} className="text-white/45 hover:text-fa-sky">{p.club}</Link>
                                    {p.notes && <span className="text-white/55 basis-full">{p.notes}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {winners.length > 0 && (
                <div data-testid="preseason-tips-winners">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/45 mb-1.5">FPL vítězové posledních přáteláků</div>
                    <div className="space-y-1">
                        {winners.map((m) => (
                            <p key={m.unique_key} className="font-mono text-[10px] text-white/70">
                                <Link to={`/preseason/club/${m.club_key}`} className="text-fa-sky hover:underline">{m.club}</Link>
                                <span className="text-white/40"> vs {m.opponent} · {m.date}</span> — <span className="text-fa-mint/90">{m.fpl_winners}</span>
                            </p>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

function TeamDetail({ team }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const load = useCallback(() => {
        axios.get(`${API}/preseason/team/${encodeURIComponent(team)}`).then((r) => setData(r.data)).catch(() => {});
    }, [team]);
    useEffect(() => { setData(null); load(); }, [load]);
    const del = async (id) => {
        try {
            await axios.delete(`${API}/preseason/match/${id}`);
            toast.success("Zápas smazán");
            load();
        } catch { toast.error("Smazání selhalo"); }
    };
    if (!data) return <p className="font-mono text-[10px] text-white/40">Načítám…</p>;
    return (
        <div className="grid lg:grid-cols-2 gap-5" data-testid="preseason-team-detail">
            <div>
                <h3 className="font-display font-black uppercase text-sm mb-2">Minutáž hráčů ({data.players.length})</h3>
                <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="font-mono text-[8px] uppercase tracking-widest text-white/40 border-b border-white/10">
                                <th className="px-3 py-1.5">#</th><th className="px-3 py-1.5">Hráč</th>
                                <th className="px-3 py-1.5 text-right">Minuty</th>
                                <th className="px-3 py-1.5 text-right">% možných</th>
                                <th className="px-3 py-1.5 text-right">Starty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.players.map((pl) => (
                                <tr key={pl.player_name} className="border-b border-white/5 font-mono text-[11px] tabular-nums" data-testid={`preseason-player-${pl.rank}`}>
                                    <td className="px-3 py-1.5 text-white/40">{pl.rank}</td>
                                    <td className="px-3 py-1.5">
                                        {pl.fpl_id
                                            ? <Link to={`/players/${pl.fpl_id}`} className="font-display font-bold text-white/90 hover:text-fa-mint">{pl.player_name}</Link>
                                            : <span className="text-white/60">{pl.player_name}</span>}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-fa-mint">{pl.minutes}</td>
                                    <td className="px-3 py-1.5 text-right">{pl.share_pct != null ? `${pl.share_pct}%` : "—"}</td>
                                    <td className="px-3 py-1.5 text-right text-white/60">{pl.starts}/{pl.matches}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div>
                <h3 className="font-display font-black uppercase text-sm mb-2">Přáteláky</h3>
                <div className="space-y-1.5">
                    {data.matches.map((m) => (
                        <div key={m.espn_id} className="border border-white/10 bg-[#0d0d0d] px-3 py-2 flex items-center justify-between font-mono text-[11px]">
                            <span className="text-white/70">{m.date} · {m.name}{m.manual && <span className="text-fa-cream/70 text-[8px] uppercase ml-1.5 border border-fa-violet/50 px-1">ručně</span>}</span>
                            <span className="flex items-center gap-2">
                                <span className="font-bold tabular-nums text-fa-yellow">{m.finished ? `${m.home_score}:${m.away_score}` : "plán"}</span>
                                {user?.is_admin && m.manual && (
                                    <button type="button" onClick={() => del(m.espn_id)} data-testid={`preseason-del-${m.espn_id}`}
                                            className="text-white/30 hover:text-red-400">×</button>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Preseason() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [coaches, setCoaches] = useState(null);
    const [team, setTeam] = useState(null);
    const [busy, setBusy] = useState(false);
    const [img, setImg] = useState(null);
    const [manualText, setManualText] = useState("");

    const submitManual = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/preseason/manual`,
                { image_base64: img, text: manualText || null }, { timeout: 120000 });
            toast.success(`Uloženo: ${r.data.match} · ${r.data.minutes_rows} záznamů minut`);
            setImg(null); setManualText("");
            load();
            if (r.data.fpl_teams?.[0]) setTeam(r.data.fpl_teams[0]);
        } catch (e) { toast.error(e.response?.data?.detail || "Vložení selhalo"); }
        finally { setBusy(false); }
    };

    const load = useCallback(() => {
        axios.get(`${API}/preseason/overview`).then((r) => setData(r.data)).catch(() => {});
        axios.get(`${API}/coaches/overview`).then((r) => setCoaches(r.data)).catch(() => {});
    }, []);
    useEffect(load, [load]);

    const sync = async (what) => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/${what}/sync`, {}, { timeout: 240000 });
            toast.success(what === "preseason"
                ? `Synchronizováno: ${r.data.matches} zápasů, ${r.data.players} hráčů`
                : `Trenéři: ${r.data.teams} týmů, noví: ${r.data.new_coaches.length}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Sync selhal"); }
        finally { setBusy(false); }
    };

    return (
        <AppShell>
            <div className="space-y-6" data-testid="preseason-page">
                <header className="border-2 border-fa-sky/40 bg-gradient-to-br from-fa-sky/10 via-transparent to-transparent p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-sky flex items-center gap-2">
                        <Barbell size={11} weight="fill" /> Manager Lab · Analyze
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Model přípravy</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Přátelské zápasy PL týmů, minutáž hráčů v přípravě a noví trenéři. Vysoká přípravná minutáž zvyšuje pravděpodobnost startů v Prediction Modelu; nový trenér snižuje jistotu.
                    </p>
                    <Link to="/preseason/vs-reality" data-testid="preseason-vsr-link"
                          className="inline-flex items-center gap-1.5 mt-3 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black">
                        <TrendUp size={10} /> Očekávání vs. realita <ArrowRight size={10} />
                    </Link>
                    {user?.is_admin && (
                        <div className="flex gap-2 mt-3">
                            <button type="button" onClick={() => sync("preseason")} disabled={busy} data-testid="preseason-sync-btn"
                                    className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-40 flex items-center gap-1">
                                <ArrowsClockwise size={10} className={busy ? "animate-spin" : ""} /> Sync přáteláky (ESPN)
                            </button>
                            <button type="button" onClick={() => sync("coaches")} disabled={busy} data-testid="coaches-sync-btn"
                                    className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#FF8800]/60 text-[#FF8800] hover:bg-[#FF8800] hover:text-black disabled:opacity-40 flex items-center gap-1">
                                <Megaphone size={10} /> Sync trenéři (SportMonks)
                            </button>
                        </div>
                    )}
                </header>

                {/* Admin: ruční vložení přáteláku (AI screenshot/text) */}
                {user?.is_admin && (
                    <section className="border border-fa-violet/40 bg-fa-violet/5 p-4 space-y-2" data-testid="preseason-manual-panel">
                        <p className="font-mono text-[9px] uppercase tracking-widest text-fa-cream">Přidat přátelák — vlož screenshot (sestavy/střídání/výsledek) nebo text, AI vytěží vše sama</p>
                        <div className="flex flex-wrap gap-3 items-start">
                            <label className="font-mono text-[10px] text-white/60 border border-white/20 px-3 py-2 cursor-pointer hover:border-fa-cream">
                                {img ? "✓ Screenshot vybrán" : "Vybrat screenshot…"}
                                <input type="file" accept="image/*" className="hidden" data-testid="preseason-file"
                                       onChange={(e) => {
                                           const f = e.target.files?.[0];
                                           if (!f) return;
                                           const rd = new FileReader();
                                           rd.onload = () => setImg(rd.result);
                                           rd.readAsDataURL(f);
                                       }} />
                            </label>
                            {img && <img src={img} alt="náhled" className="h-16 border border-white/15" />}
                            <textarea value={manualText} onChange={(e) => setManualText(e.target.value)} rows={2}
                                      placeholder="…nebo sem vlož text zápasu (sestavy, střídání, skóre)"
                                      data-testid="preseason-manual-text"
                                      className="flex-1 min-w-[240px] bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] resize-y" />
                            <button type="button" onClick={submitManual} disabled={busy || (!img && manualText.trim().length < 30)}
                                    data-testid="preseason-manual-submit"
                                    className="font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border border-fa-violet/70 text-fa-cream hover:bg-fa-violet disabled:opacity-40">
                                {busy ? "AI analyzuje…" : "✦ Analyzovat a uložit"}
                            </button>
                        </div>
                    </section>
                )}

                {/* Redakční scouting — tipy na hráče */}
                <ScoutingTips />

                {/* Noví trenéři */}
                {coaches?.coaches?.length > 0 && (
                    <section data-testid="coaches-section">
                        <h2 className="font-display font-black uppercase text-base mb-2 flex items-center gap-1.5">
                            <Megaphone size={14} className="text-[#FF8800]" /> Trenéři PL
                            <span className="font-mono text-[9px] text-white/40 normal-case tracking-normal">nový = nástup po {coaches.cutoff}</span>
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                            {coaches.coaches.map((c) => (
                                <div key={c.team} className={`border px-3 py-2 ${c.is_new ? "border-[#FF8800]/60 bg-[#FF8800]/5" : "border-white/10 bg-[#0d0d0d]"}`} data-testid={`coach-${c.team.replace(/[^a-zA-Z]/g, "")}`}>
                                    <div className="font-display font-bold text-xs">{c.team}</div>
                                    <div className="font-mono text-[10px] text-white/65">{c.coach}</div>
                                    <div className="font-mono text-[8px] text-white/40">od {c.start}{c.is_new && <span className="text-[#FF8800] uppercase font-bold ml-1.5">Nový</span>}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Týmy s přáteláky */}
                <section>
                    <h2 className="font-display font-black uppercase text-base mb-2">Přáteláky týmů
                        {data && <span className="font-mono text-[9px] text-white/40 ml-2 normal-case tracking-normal">{data.total_matches} zápasů · {data.players_tracked} hráčů se záznamem minut</span>}
                    </h2>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                        {(data?.teams || []).map((t) => (
                            <button key={t.team} type="button" onClick={() => setTeam(t.team)} data-testid={`preseason-team-${t.team.replace(/[^a-zA-Z]/g, "")}`}
                                    className={`font-mono text-[10px] px-2.5 py-1.5 border ${team === t.team ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}>
                                {t.team} ({t.matches.length})
                            </button>
                        ))}
                        {data && data.teams.length === 0 && <p className="font-mono text-[10px] text-white/40">Zatím žádné zápasy — spusť sync.</p>}
                    </div>
                    {team && <TeamDetail team={team} />}
                </section>
            </div>
        </AppShell>
    );
}
